import { cloneDeep } from "lodash";
import { getVariationNumber } from "../common";
import { roundValue, toDecimal } from "../decimal-utils";

export const optionsTotal = ({
  options,
  projectType,
  isviewVariationFilter = false,
}) => {
  let filteredOptions = cloneDeep(options);
  let _options = cloneDeep(options);

  if (!isviewVariationFilter) {
    _options = getVariationNumber(options);
    filteredOptions = _options;

    if (projectType === "PROJECT") {
      filteredOptions = options.filter(
        (option) => option.proposal_status === "APPROVED"
      );
    }
  }

  const stageTotal = {};
  const viewVariation = {};

  const baseTotals = {
    materialCost: 0.0,
    laborCost: 0.0,
    subcontractorCost: 0.0,
    materialSaleAmount: 0.0,
    laborSaleAmount: 0.0,
    subcontractorSaleAmount: 0.0,
    totalSaleAmount: 0.0,
    totalCost: 0.0,
    materialProfit: 0.0,
    laborProfit: 0.0,
    subcontractorProfit: 0.0,
    totalProfit: 0.0,
    materialProfitPercentage: 0.0,
    laborProfitPercentage: 0.0,
    subcontractorProfitPercentage: 0.0,
    totalProfitPercentage: 0.0,
    percentageComplete: 0.0,
    forecastHours: 0.0,
    actualHours: 0.0,
    taskCount: 0,
  };

  const projectTotal = { ...baseTotals };
  const viewVariationProjectTotal = { ...baseTotals };

  const defaultOptionTotals = {
    ...baseTotals,
    parentStageId: null,
    stageId: null,
    name: "",
    optionDescription: "",
  };

  const toPercent = (profit, cost) => {
    const _profit = toDecimal(profit);
    const _cost = toDecimal(cost);
    if (_cost.isZero()) return 0.0;
    return _profit.dividedBy(_cost).times(100).toNumber();
  };

  const initializeTotals = (target, id, parentId = null, optionData = {}) => {
    if (!target[id]) {
      target[id] = {
        ...defaultOptionTotals,
        parentStageId: parentId,
        stageId: id,
        name: optionData.name || "",
        optionDescription: optionData.description || "",
      };
    }
  };

  // ──────────────────────────────────────────────
  // 🔸 MAIN OPTION LOOP (Approved/Filtered)
  // ──────────────────────────────────────────────
  Array.isArray(filteredOptions) &&
    filteredOptions.forEach((option) => {
      const stageId = option.stageId || "projectlevel";
      const optionId = option.id;

      initializeTotals(
        stageTotal,
        stageId,
        option.parentStageId || null,
        option
      );

      if (!stageTotal[stageId].optionList) {
        stageTotal[stageId].optionList = {};
      }

      const optionData = {
        materialCost: roundValue(option.materialTotal_Cost || 0.0),
        laborCost: roundValue(option.laborTotal_Cost || 0.0),
        subcontractorCost: roundValue(option.subcontractorTotal_Cost || 0.0),
        materialSaleAmount: roundValue(option.materialTotal_SaleAmount || 0.0),
        laborSaleAmount: roundValue(option.laborTotal_SaleAmount || 0.0),
        subcontractorSaleAmount: roundValue(
          option.subcontractorTotal_SaleAmount || 0.0
        ),
        materialProfit: roundValue(option.material_Profit || 0.0),
        laborProfit: roundValue(option.labor_Profit || 0.0),
        subcontractorProfit: roundValue(option.subcontractor_Profit || 0.0),
        totalProfit: roundValue(
          option.material_Profit +
          option.labor_Profit +
          option.subcontractor_Profit
        ),
        percentageComplete: roundValue(option.percentageComplete || 0.0),
        forecastHours: roundValue(option.forecastHours || 0.0),
        actualHours: roundValue(option.actualHours || 0.0),
        name: option.name || "",
        optionDescription: option.description || "",
        revisionName: option.revisionName || "",
        revisionNumber: option.revisionNumber || "",
        id: option.id,
        proposal_status: option.proposal_status,
        proposal_selected: option.proposal_selected,
        approvalDate: option.approvalDate,
      };

      optionData.totalSaleAmount =
        roundValue(optionData.materialSaleAmount, 2) +
        roundValue(optionData.laborSaleAmount, 2) +
        roundValue(optionData.subcontractorSaleAmount, 2);
      optionData.totalCost = roundValue(
        optionData.materialCost +
        optionData.laborCost +
        optionData.subcontractorCost
      );

      stageTotal[stageId].optionList[optionId] = optionData;

      // 🔹 Aggregate into stage totals
      Object.keys(baseTotals).forEach((key) => {
        if (typeof optionData[key] === "number") {
          stageTotal[stageId][key] = roundValue(
            stageTotal[stageId][key] + optionData[key]
          );
        }
      });

      stageTotal[stageId].taskCount += 1;
    });

  // ──────────────────────────────────────────────
  // 🔸 VIEW VARIATION (All Options)
  // ──────────────────────────────────────────────
  Array.isArray(_options) &&
    _options.forEach((option) => {
      const optionId = option.id;
      const optionData = {
        materialCost: roundValue(option.materialTotal_Cost || 0.0),
        laborCost: roundValue(option.laborTotal_Cost || 0.0),
        subcontractorCost: roundValue(option.subcontractorTotal_Cost || 0.0),
        materialSaleAmount: roundValue(option.materialTotal_SaleAmount || 0.0),
        laborSaleAmount: roundValue(option.laborTotal_SaleAmount || 0.0),
        subcontractorSaleAmount: roundValue(
          option.subcontractorTotal_SaleAmount || 0.0
        ),
        materialProfit: roundValue(option.material_Profit || 0.0),
        laborProfit: roundValue(option.labor_Profit || 0.0),
        subcontractorProfit: roundValue(option.subcontractor_Profit || 0.0),
        totalProfit: roundValue(
          (option.material_Profit || 0) +
          (option.labor_Profit || 0) +
          (option.subcontractor_Profit || 0)
        ),
        percentageComplete: roundValue(option.percentageComplete || 0.0),
        forecastHours: roundValue(option.forecastHours || 0.0),
        actualHours: roundValue(option.actualHours || 0.0),
        name: option.name || "",
        optionDescription: option.description || "",
        revisionName: option.revisionName || "",
        revisionNumber: option.revisionNumber || "",
        id: option.id,
        proposal_selected: option.proposal_selected,
        proposal_status: option.proposal_status,
        approvalDate: option.approvalDate,
      };

      optionData.totalSaleAmount =
        roundValue(optionData.materialSaleAmount, 2) +
        roundValue(optionData.laborSaleAmount, 2) +
        roundValue(optionData.subcontractorSaleAmount, 2);
      optionData.totalCost = roundValue(
        optionData.materialCost +
        optionData.laborCost +
        optionData.subcontractorCost
      );

      optionData.materialProfitPercentage = roundValue(
        toPercent(optionData.materialProfit, optionData.materialCost)
      );
      optionData.laborProfitPercentage = roundValue(
        toPercent(optionData.laborProfit, optionData.laborCost)
      );
      optionData.subcontractorProfitPercentage = roundValue(
        toPercent(optionData.subcontractorProfit, optionData.subcontractorCost)
      );
      optionData.totalProfitPercentage = roundValue(
        toPercent(optionData.totalProfit, optionData.totalCost)
      );

      viewVariation[optionId] = optionData;

      // Aggregate into viewVariation project totals
      Object.keys(baseTotals).forEach((key) => {
        if (typeof optionData[key] === "number") {
          viewVariationProjectTotal[key] = roundValue(
            viewVariationProjectTotal[key] + optionData[key]
          );
        }
      });

      viewVariationProjectTotal.taskCount += 1;
    });

  // ──────────────────────────────────────────────
  // 🔸 STAGE SUMMARY & PROJECT TOTAL AGGREGATION
  // ──────────────────────────────────────────────
  Object.keys(stageTotal).forEach((stageId) => {
    const summary = stageTotal[stageId];
    if (summary.taskCount > 0) {
      summary.materialProfitPercentage = roundValue(
        toPercent(summary.materialProfit, summary.materialCost)
      );
      summary.laborProfitPercentage = roundValue(
        toPercent(summary.laborProfit, summary.laborCost)
      );
      summary.subcontractorProfitPercentage = roundValue(
        toPercent(summary.subcontractorProfit, summary.subcontractorCost)
      );
      summary.totalProfitPercentage = roundValue(
        toPercent(summary.totalProfit, summary.totalCost)
      );
      summary.percentageComplete = roundValue(
        summary.percentageComplete / summary.taskCount
      );
    }

    if (summary.parentStageId === null) {
      Object.keys(baseTotals).forEach((key) => {
        if (typeof summary[key] === "number") {
          projectTotal[key] = roundValue(projectTotal[key] + summary[key]);
        }
      });
      projectTotal.taskCount += summary.taskCount;
    }
  });

  // ──────────────────────────────────────────────
  // 🔸 PROJECT TOTAL PERCENTAGES
  // ──────────────────────────────────────────────
  if (projectTotal.taskCount > 0) {
    projectTotal.materialProfitPercentage = roundValue(
      toPercent(projectTotal.materialProfit, projectTotal.materialCost)
    );
    projectTotal.laborProfitPercentage = roundValue(
      toPercent(projectTotal.laborProfit, projectTotal.laborCost)
    );
    projectTotal.subcontractorProfitPercentage = roundValue(
      toPercent(
        projectTotal.subcontractorProfit,
        projectTotal.subcontractorCost
      )
    );
    projectTotal.totalProfitPercentage = roundValue(
      toPercent(projectTotal.totalProfit, projectTotal.totalCost)
    );
    projectTotal.percentageComplete = roundValue(
      projectTotal.percentageComplete / projectTotal.taskCount
    );
  }

  if (viewVariationProjectTotal.taskCount > 0) {
    viewVariationProjectTotal.materialProfitPercentage = roundValue(
      toPercent(
        viewVariationProjectTotal.materialProfit,
        viewVariationProjectTotal.materialCost
      )
    );
    viewVariationProjectTotal.laborProfitPercentage = roundValue(
      toPercent(
        viewVariationProjectTotal.laborProfit,
        viewVariationProjectTotal.laborCost
      )
    );
    viewVariationProjectTotal.subcontractorProfitPercentage = roundValue(
      toPercent(
        viewVariationProjectTotal.subcontractorProfit,
        viewVariationProjectTotal.subcontractorCost
      )
    );
    viewVariationProjectTotal.totalProfitPercentage = roundValue(
      toPercent(
        viewVariationProjectTotal.totalProfit,
        viewVariationProjectTotal.totalCost
      )
    );
    viewVariationProjectTotal.percentageComplete = roundValue(
      viewVariationProjectTotal.percentageComplete /
      viewVariationProjectTotal.taskCount
    );
  }

  viewVariation.projectTotal = viewVariationProjectTotal;

  return {
    stageSummary: stageTotal,
    projectTotal,
    optionList: viewVariation,
  };
};
